import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { singOut } from "../slices/userSlice";

const baseApi = process.env.REACT_APP_API_BASE;

// Base query normal
const baseQuery = fetchBaseQuery({
  baseUrl: baseApi,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().user.user?.token;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    headers.set("Content-Type", "application/json");
    return headers;
  },
});

const baseQueryWithAuth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    api.dispatch(singOut());
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithAuth,

  tagTypes: ["Company", "Templates", "TemplatesHeaderFooter", "User"],

  endpoints: (builder) => ({
    // =========================
    // USER
    // =========================
    getUserById: builder.query({
      query: (userId) => `user/getUsersById?userId=${userId}`,
      providesTags: ["User"],
    }),

    // =========================
    // COMPANY
    // =========================
    getCompany: builder.query({
      query: (companyId) => `company/byId?companyId=${companyId}`,
      providesTags: ["Company"],
    }),

    // =========================
    // TEMPLATES
    // =========================
    getTemplates: builder.query({
      query: (companyId) =>
        `template/getTemplatesByCompany?companyId=${companyId}`,
      providesTags: ["Templates"],
    }),

    insertTemplate: builder.mutation({
      query: (body) => ({
        url: "template/insertTemplate",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Templates"],
    }),

    updateTemplate: builder.mutation({
      query: ({ templateId, body }) => ({
        url: `template/updateTemplate?templateId=${templateId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Templates"],
    }),

    // =========================
    // TemplatesHeaderFooter
    // =========================
    getTemplatesHeaderFooter: builder.query({
      query: (companyId) =>
        `templateHeaderFooter/getByCompany?companyId=${companyId}`,
      providesTags: ["TemplatesHeaderFooter"],
    }),

    insertTemplateHeaderFooter: builder.mutation({
      query: (body) => ({
        url: "templateHeaderFooter/insert",
        method: "POST",
        body,
      }),
      invalidatesTags: ["TemplatesHeaderFooter"],
    }),

    updateTemplateHeaderFooter: builder.mutation({
      query: ({ templateId, body }) => ({
        url: `templateHeaderFooter/update?id=${templateId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["TemplatesHeaderFooter"],
    }),
  }),
});

export const {
  useGetUserByIdQuery,
  useGetCompanyQuery,
  useGetTemplatesQuery,
  useInsertTemplateMutation,
  useUpdateTemplateMutation,
  useGetTemplatesHeaderFooterQuery,
  useInsertTemplateHeaderFooterMutation,
  useUpdateTemplateHeaderFooterMutation,
} = apiSlice;
